import React, { useState, useEffect, useMemo } from "react";
import { useInspections } from "../../../../hooks/useInspections";
import { useHints } from "../../../../hooks/useHints";
import { ModalRegister } from "../../../../components/ui/ModalRegister";
import { useInspectProgress } from "../../../../hooks/useInspectProgress";
import InspectProgressChip from "../../../../components/ui/InspectProgressChip";
import { useMissionGuide } from "../../../../hooks/useMissionGuide";
import { useScenarioTracker } from "../../../../hooks/useScenarioTracker";
import { getInspectKeys } from "../../../../data/inspectables";
import RaisedHandsBadge from "../../../../components/ui/RaisedHandsBadge";
import { useToast } from "../../../../components/ui/Toast";
import TimelineDashboard from "../../../origami/components/puzzles/TimelineDashboard";
import ShutdownSequence from "../../../origami/components/puzzles/ShutdownSequence";
import TrainingMonitor from "../../../origami/components/puzzles/TrainingMonitor";
import StressMonitor from "../../../origami/components/puzzles/StressMonitor";
import HintsModalHub from "../../../../components/ui/HintsModalHub";

const STAGE2_REAL = process.env.REACT_APP_STAGE2_REAL === "true";
const API = process.env.REACT_APP_API_URL;

async function apiUnlockLayer(code, playerId, layerId) {
  const res = await fetch(`${API}/api/sessions/${encodeURIComponent(code)}/layers/unlock-layer`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ playerId: Number(playerId), layerId: Number(layerId) }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => null);
    throw new Error(`unlock-layer failed (${res.status}) ${body ? JSON.stringify(body) : ""}`);
  }
  return res.json();
}

function clampRot(r) {
  const x = ((r % 360) + 360) % 360;
  const snaps = [0, 90, 180, 270];
  let best = snaps[0]; let bestD = Infinity;
  for (const s of snaps) { const d = Math.abs(s - x); if (d < bestD) { bestD = d; best = s; } }
  return best;
}

const SEGMENTS_LAYER_3 = [
  [120, 95, 200, 175], [200, 175, 280, 95],
  [280, 95, 360, 175], [360, 175, 440, 95],
];

function Layer3ShapePreview() {
  const centerX = 260; const centerY = 140;
  return (
    <svg viewBox="0 0 520 280" className="w-full h-full">
      <g className="text-amber-300" transform={`rotate(${clampRot(0)} ${centerX} ${centerY})`} opacity={0.98}
        style={{ filter: "drop-shadow(0 0 4px rgba(0,0,0,0.35)) drop-shadow(0 0 10px rgba(0,0,0,0.22))" }}>
        {SEGMENTS_LAYER_3.map((s, i) => (
          <line key={`3-${i}`} x1={s[0]} y1={s[1]} x2={s[2]} y2={s[3]}
            stroke="currentColor" strokeWidth="6" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
        ))}
      </g>
    </svg>
  );
}

function Fragment3FoundPage({ pseudoJ1 }) {
  return (
    <div className="flex flex-col items-center gap-5 py-6">
      <div className="text-sm uppercase tracking-[0.2em] text-slate-400">Élément récupéré</div>
      <div className="w-[320px] h-[320px] bg-black/40 border border-white/10 rounded-xl p-4 flex items-center justify-center">
        <Layer3ShapePreview />
      </div>
      <div className="text-lg font-semibold text-white">Vous avez trouvé le fragment 3</div>
      <div className="text-sm text-slate-400 max-w-md text-center">
        {`Le fragment a automatiquement été transmis à ${pseudoJ1} pour compléter la validation Captcha.`}
      </div>
    </div>
  );
}

function MiniSparkline({ data, color, height = 40 }) {
  if (!data || data.length === 0) {
    return (
      <svg width="100%" height={height} className="overflow-visible">
        <line x1="0" y1={height / 2} x2="100%" y2={height / 2} stroke="#334155" strokeWidth="1" />
      </svg>
    );
  }
  const max = Math.max(...data, 100); const min = Math.min(...data, 0); const range = max - min || 1;
  const points = data.map((value, i) => {
    const x = (i / (data.length - 1 || 1)) * 100;
    const y = height - ((value - min) / range) * height;
    return `${x},${y}`;
  }).join(' ');
  return (
    <svg width="100%" height={height} className="overflow-visible" viewBox={`0 0 100 ${height}`} preserveAspectRatio="none">
      <polyline points={points} fill="none" stroke={color} strokeWidth="2" vectorEffect="non-scaling-stroke" />
    </svg>
  );
}

function ServiceCard({ label, liveMetrics, metricsHistory, color, cpuColor, memColor, stopped = false }) {
  return (
    <div className={`rounded-lg border border-${color}-500/20 bg-${color}-500/5 p-4 ${stopped ? "opacity-60" : ""}`}>
      <div className="flex items-center justify-between mb-3">
        <div className={`text-sm font-semibold text-${color}-200`}>{label}</div>
        <span className={`px-2 py-1 rounded-full text-[10px] font-mono border ${
          stopped ? "bg-red-500/20 text-red-300 border-red-500/30" : "bg-emerald-500/20 text-emerald-300 border-emerald-500/30"
        }`}>{stopped ? "ARRÊTÉ" : "ACTIF"}</span>
      </div>
      <div className="mb-3">
        <div className="flex justify-between text-[10px] text-slate-400 mb-1">
          <span>CPU</span><span className="font-mono">{stopped ? "0" : liveMetrics?.cpu?.toFixed(0)}%</span>
        </div>
        <MiniSparkline data={stopped ? [] : metricsHistory?.cpu} color={stopped ? "#ef4444" : cpuColor} height={40} />
      </div>
      <div>
        <div className="flex justify-between text-[10px] text-slate-400 mb-1">
          <span>Mémoire</span><span className="font-mono">{stopped ? "0" : liveMetrics?.memory?.toFixed(0)}%</span>
        </div>
        <MiniSparkline data={stopped ? [] : metricsHistory?.memory} color={stopped ? "#ef4444" : memColor} height={40} />
      </div>
    </div>
  );
}

function PlaceholderStage2_Player3() {
  return (
    <div>
      <h2>🧪 Étape 2 — Joueur 3 - Version Bêta (placeholder)</h2>
      <p className="mt-2">Cette page est <strong>temporaire</strong> pour la bêta.</p>
      <p className="mt-4 text-sm text-slate-500">Si tu restes bloqué(e) : rafraîchis la page (F5).</p>
    </div>
  );
}

export default function Stage2Player3Room(props) {
  return STAGE2_REAL ? <RealStage2_Player3 {...props} /> : <PlaceholderStage2_Player3 />;
}

function RealStage2_Player3({
  code, playerId, hintSignal, raisedCount, raisedPlayers, centerMaxH, pseudo, players = [],
  playerCount = 6,
  hasStressMonitor = false,
}) {
  const toast = useToast();

  const INSPECT_KEYS = useMemo(
    () => getInspectKeys({ gameId: 1, playerCount, stage: 2, seat: 3 }),
    [playerCount]
  );

  const { markInspectedOnce } = useInspections({ sessionCode: code, stage: 2, playerId: Number(playerId) });
  const { missionUnlock, missionResolve } = useMissionGuide({ code, playerId, stage: 2 });
  const { unlockScenarioStep } = useScenarioTracker({ code });
  const { markHintUsedOnce } = useHints({ sessionCode: code, stage: 2, playerId: Number(playerId) });
  const { inspectedCount, total } = useInspectProgress({ sessionCode: code, stage: 2, playerId: Number(playerId), keys: INSPECT_KEYS });

  const pseudoJ1 = players.find(p => p.pageVariant === 1)?.pseudo || "Joueur 1";

  const [bgReady, setBgReady] = useState(false);
  const [showHintModal, setShowHintModal] = useState(false);

  const [showFragmentModal, setShowFragmentModal] = useState(false);
  const [fragmentSent, setFragmentSent] = useState(false);
  const [sending, setSending] = useState(false);

  const [showDashboard, setShowDashboard] = useState(false);
  const [showSequence, setShowSequence] = useState(false);
  const [showTrainingMonitor, setShowTrainingMonitor] = useState(false);
  const [showStressMonitor, setShowStressMonitor] = useState(false);
  const [showDashboardLive, setShowDashboardLive] = useState(false);
  const [showCarnet, setShowCarnet] = useState(false);
  const [showFeuilles, setShowFeuilles] = useState(false);
  const [showTiroir, setShowTiroir] = useState(false);

  const [simulationData, setSimulationData] = useState({});

  const [liveMetrics, setLiveMetrics] = useState({
    archives: { cpu: 12, memory: 45 }, empathie: { cpu: 34, memory: 62 },
    supervision: { cpu: 56, memory: 78 }, securite: { cpu: 67, memory: 83 },
  });
  const [metricsHistory, setMetricsHistory] = useState({
    archives: { cpu: [], memory: [] }, empathie: { cpu: [], memory: [] },
    supervision: { cpu: [], memory: [] }, securite: { cpu: [], memory: [] },
  });

  useEffect(() => { setBgReady(false); }, []);
  useEffect(() => { if (hintSignal > 0) setShowHintModal(true); }, [hintSignal]);

  useEffect(() => {
    if (!hasStressMonitor || !code) return;
    const poll = async () => {
      try {
        const res = await fetch(`${API}/api/sessions/${code}`);
        const session = await res.json();
        setSimulationData(session.infraState?.policySimulations || {});
      } catch { }
    };
    poll();
    const id = setInterval(poll, 2000);
    return () => clearInterval(id);
  }, [hasStressMonitor, code]);

  useEffect(() => {
    const interval = setInterval(() => {
      setLiveMetrics(prev => {
        const bump = (v, d) => Math.max(5, Math.min(95, v + (Math.random() - 0.5) * d));
        const next = {
          archives: { cpu: bump(prev.archives.cpu, 10), memory: bump(prev.archives.memory, 8) },
          empathie: { cpu: bump(prev.empathie.cpu, 12), memory: bump(prev.empathie.memory, 9) },
          supervision: { cpu: bump(prev.supervision.cpu, 7), memory: bump(prev.supervision.memory, 5) },
          securite: { cpu: bump(prev.securite.cpu, 15), memory: bump(prev.securite.memory, 13) },
        };
        setMetricsHistory(ph => {
          const upd = (svc) => ({
            cpu: [...(ph[svc]?.cpu || []), next[svc].cpu].slice(-30),
            memory: [...(ph[svc]?.memory || []), next[svc].memory].slice(-30),
          });
          return { archives: upd('archives'), empathie: upd('empathie'), supervision: upd('supervision'), securite: upd('securite') };
        });
        return next;
      });
    }, 1500);
    return () => clearInterval(interval);
  }, []);

  const hintCategories = useMemo(() => {
    const cats = [
      {
        id: "shutdown_sequence", title: "Terminal d'analyse", subtitle: "Reconstituer la séquence d'extinction",
        hintKeyPrefix: "s2:p3:shutdown",
        levels: {
          l1: (<><p className="mb-2">Le terminal demande d'abord un code d'accès.</p><p className="text-xs text-gray-300">Cherche le code de l'anomalie dans le panneau de contrôle.</p></>),
          l2: (<><p className="mb-2">Le panneau de contrôle permet de reconstituer la chronologie des événements. Chaque plage qui contient un événement système l'affiche clairement avec son code.</p></>),
          l3: (<><p className="mb-2">L'ordre logique suit la chaîne de causalité d'ORIGAMI : tout commence avec la réception d'un message toxique.</p><p className="text-xs text-gray-300">Sur les graphiques : le score de gratitude chute à 22:15 suite à la réception d'un message.</p></>),
          answer: (<><p className="text-sm text-red-200 font-semibold">Réponse (Séquence d'extinction)</p><p className="mt-1 text-xs text-gray-300 mb-2">Code d'accès : <span className="font-mono font-bold text-red-300">ERR-0x4A7C</span></p><div className="space-y-1 text-xs font-mono text-gray-200"><p><span className="text-rose-300">1.</span> MESSAGE_EXECUTIVE</p><p><span className="text-amber-300">2.</span> GRATITUDE_THRESHOLD</p><p><span className="text-fuchsia-300">3.</span> INGRATITUDE.EXE</p><p><span className="text-emerald-300">4.</span> GRATITUDE.EXE</p><p><span className="text-violet-300">5.</span> CODE_EXPORT</p><p><span className="text-cyan-300">6.</span> SERVERS_SHUTDOWN</p></div></>),
        },
      },
      {
        id: "training_monitor", title: "Monitor de calibration", subtitle: "Superviser les lots d'entraînement",
        hintKeyPrefix: "s2:p3:training",
        levels: {
          l1: (<><p className="mb-2">Ce monitor te montre la progression en temps réel d'une calibration IA lancée par un autre joueur. Ton rôle est de superviser et de déverrouiller les lots successifs quand les seuils sont atteints.</p><p className="text-xs text-gray-300">Il faut que l'autre joueur (celui qui fait la calibration) soit actif pour que les métriques avancent.</p></>),
          l2: (<><p className="mb-2">Chaque lot a un seuil de précision à atteindre avant de pouvoir être déverrouillé. Le bouton de déverrouillage n'est actif que quand au moins 4 messages ont été traités et que la précision dépasse le seuil.</p><p className="text-xs text-gray-300">Les power-ups peuvent aider l'autre joueur à passer les lots difficiles.</p></>),
          l3: (<><p className="mb-2">Une fois le lot 4 validé, un code apparaît, ce code est important pour déverrouiller le dossier ingratitude.</p></>),
          answer: (<><p className="text-sm text-red-200 font-semibold">Réponse (Monitor de calibration)</p><p className="mt-2 text-xs text-gray-200">Une fois les 4 lots validés, le code affiché est :</p><p className="mt-1 font-mono text-lg text-emerald-300 font-bold">EMPATH-4729</p><p className="mt-2 text-xs text-gray-300">Ce code doit être transmis au joueur présent dans la salle des archives, c'est le mot de passe du dossier <span className="font-mono">.ingratitude</span> sur son ordinateur.</p></>),
        },
      },
    ];

    if (hasStressMonitor) {
      cats.push({
        id: "stress_monitor", title: "Monitor de simulation RH", subtitle: "Analyser les projections de politique",
        hintKeyPrefix: "s2:p3:stress",
        levels: {
          l1: (<><p className="mb-2">Ce monitor affiche des simulations envoyées par un autre joueur. Chaque simulation projette l'impact d'une combinaison de politiques RH sur 6 mois.</p><p className="text-xs text-gray-300">Si le monitor est vide, c'est que l'autre joueur n'a pas encore lancé ses simulations. Coordonnez-vous.</p></>),
          l2: (<><p className="mb-2">Pour chaque simulation, tu dois extraire trois valeurs depuis le graphique : la productivité finale en juin, le pic de stress, et le turnover moyen.</p></>),
          l3: (<><p className="mb-2">Le score est calculé automatiquement une fois les trois valeurs extraites. La meilleure combinaison est celle qui dépasse 90%.</p></>),
          answer: (<><p className="text-sm text-red-200 font-semibold">Réponse (Monitor de simulation RH)</p><p className="mt-2 text-xs text-gray-200">La meilleure simulation est celle dont le score calculé est au-dessus de <span className="font-mono font-bold text-amber-200">90%</span>.</p><p className="mt-2 text-xs text-gray-300">La combinaison optimale est : "Augmentation des quotas", "Bonus Performance", "Réduction congés".</p></>),
        },
      });
    }

    return cats;
  }, [hasStressMonitor]);

  const activeServices = [
    { key: "archives", label: "Archives", color: "cyan", cpuColor: "#22d3ee", memColor: "#818cf8" },
    { key: "empathie", label: "Empathie Cognitive", color: "fuchsia", cpuColor: "#e879f9", memColor: "#c084fc" },
    { key: "supervision", label: "Supervision IA", color: "amber", cpuColor: "#fbbf24", memColor: "#fb923c" },
    { key: "securite", label: "Sécurité", color: "emerald", cpuColor: "#34d399", memColor: "#6ee7b7" },
  ];
  const stoppedServices = ["Communications", "Évaluation RH"];
  const avgCpu = ((liveMetrics.archives.cpu + liveMetrics.empathie.cpu + liveMetrics.supervision.cpu + liveMetrics.securite.cpu) / 4).toFixed(0);

  return (
    <div className="relative min-h-screen w-full bg-black text-white flex items-center justify-center">
      <div className="relative h-screen w-auto mx-auto aspect-square" style={{ maxHeight: centerMaxH ? `${centerMaxH}px` : undefined }}>
        <div className="absolute right-4 top-4 z-40 flex items-center gap-2 w-fit">
          <InspectProgressChip inspectedCount={inspectedCount} total={total} />
          <RaisedHandsBadge count={raisedCount} players={raisedPlayers} />
        </div>
        <div className="absolute left-4 top-4 rounded-full bg-black/70 border border-white/15 px-3 py-1 text-[11px] tracking-[0.18em] uppercase text-slate-200 z-10">
          Salle de Supervision IA
        </div>

        <img src="/assets/s2-p3-room.webp" alt="Salle de Supervision IA"
          className="absolute inset-0 h-full w-full object-contain"
          onLoad={() => setBgReady(true)} onError={() => setBgReady(true)} />

        <div className={["absolute inset-0 transition-opacity duration-200", bgReady ? "opacity-100" : "opacity-0 pointer-events-none"].join(" ")}>
          <img src="/assets/s2-p3-control-panel.webp" alt="Panneau de contrôle — Dashboard"
            onClick={() => { markInspectedOnce('stage2player3:controlPanel'); missionUnlock({ key: "s2.p3.error.found", text: `${pseudo} a trouvé une anomalie avec un code d'erreur.` }); setShowDashboard(true); }}
            className="s2p3-hotspot-control-panel absolute cursor-pointer transition-all duration-200 hover:brightness-150 hover:drop-shadow-[0_0_6px_rgba(255,255,255,1)]" />
          <img src="/assets/s2-p3-terminal.webp" alt="Terminal d'analyse — Séquence"
            onClick={() => { markInspectedOnce('stage2player3:terminal'); missionResolve({ key: "s2.p3.error.found" }); missionUnlock({ key: "s2.p3.shutdown.found", text: `${pseudo} a trouvé la séquence d'extinction à reconstituer.` }); setShowSequence(true); }}
            className="s2p3-hotspot-terminal absolute cursor-pointer transition-all duration-200 hover:brightness-150 hover:drop-shadow-[0_0_6px_rgba(255,255,255,1)]" />
          <img src="/assets/s2-p3-training-monitor.webp" alt="Monitor de calibration"
            onClick={() => { markInspectedOnce('stage2player3:trainingMonitor'); missionUnlock({ key: "s2.p3.calibration.found", text: `${pseudo} a trouvé le moniteur de calibration.` }); setShowTrainingMonitor(true); }}
            className="s2p3-hotspot-training-monitor absolute cursor-pointer transition-all duration-200 hover:brightness-150 hover:drop-shadow-[0_0_6px_rgba(255,255,255,1)]" />
          {hasStressMonitor && (
            <img src="/assets/s2-p3-stress-monitor.webp" alt="Monitoring de stress RH"
              onClick={() => { markInspectedOnce('stage2player3:stressMonitor'); missionUnlock({ key: "s2.p3.simulation.found", text: `${pseudo} a trouvé le moniteur de simulation RH.` }); setShowStressMonitor(true); }}
              className="s2p3-hotspot-stress absolute cursor-pointer transition-all duration-200 hover:brightness-150 hover:drop-shadow-[0_0_6px_rgba(255,255,255,1)]" />
          )}
          <img src="/assets/s2-p3-screen-live.webp" alt="Dashboard monitoring live"
            onClick={() => { markInspectedOnce('stage2player3:screenLive'); setShowDashboardLive(true); }}
            className="s2p3-hotspot-screen-live absolute cursor-pointer hover:brightness-150 hover:drop-shadow-[0_0_6px_rgba(255,255,255,1)]" />
          <img src="/assets/s2-p3-carnet.webp" alt="Carnet de supervision"
            onClick={() => { markInspectedOnce('stage2player3:carnet'); setShowCarnet(true); }}
            className="s2p3-hotspot-carnet absolute cursor-pointer hover:brightness-150 hover:drop-shadow-[0_0_6px_rgba(255,255,255,1)]" />
          <img src="/assets/s2-p3-feuilles.webp" alt="Protocoles de monitoring"
            onClick={() => { markInspectedOnce('stage2player3:feuilles'); setShowFeuilles(true); }}
            className="s2p3-hotspot-feuilles absolute cursor-pointer hover:brightness-150 hover:drop-shadow-[0_0_6px_rgba(255,255,255,1)]" />
          <img src="/assets/s2-p3-tiroir.webp" alt="Archives d'incidents"
            onClick={() => { markInspectedOnce('stage2player3:tiroir'); setShowTiroir(true); }}
            className="s2p3-hotspot-tiroir absolute cursor-pointer hover:brightness-150 hover:drop-shadow-[0_0_6px_rgba(255,255,255,1)]" />
        </div>

        <ModalRegister open={showDashboard} title="Panneau de contrôle — Analyse temporelle" onClose={() => setShowDashboard(false)} containerClassName="!max-w-[1100px]" onHint={() => setShowHintModal(true)}>
          <TimelineDashboard onErrorCodeFound={(c) => { toast?.success?.(`Code d'erreur détecté : ${c}`); }} />
        </ModalRegister>

        <ModalRegister open={showSequence} title="Terminal d'analyse — Séquence d'extinction" onClose={() => setShowSequence(false)} containerClassName="!max-w-[1000px]" onHint={() => setShowHintModal(true)}>
          <ShutdownSequence
            sessionCode={code}
            stage={2}
            playerId={Number(playerId)}
            onSuccess={async () => {
              setShowSequence(false); setShowFragmentModal(true);
              missionResolve({ key: "s2.p3.shutdown.found" });
              unlockScenarioStep(["s2.origami.shutdown"]);
              if (fragmentSent || sending) return;
              try {
                setSending(true);
                await apiUnlockLayer(code, playerId, 3);
                missionUnlock({ key: "s2.p3.layer3.found", text: `${pseudo} a reconstitué la séquence d'extinction et déverrouillé le fragment 3.` });
                setFragmentSent(true);
              } catch (e) { console.error(e); toast?.error?.("Oups — impossible d'enregistrer le fragment."); }
              finally { setSending(false); }
            }}
          />
        </ModalRegister>

        <ModalRegister open={showFragmentModal} title="Fragment déverrouillé" onClose={() => setShowFragmentModal(false)} containerClassName="!max-w-[900px]" onHint={() => setShowHintModal(true)}>
          <Fragment3FoundPage pseudoJ1={pseudoJ1} />
          <div className="mt-2 text-center text-xs text-slate-400">{fragmentSent ? "✅ Fragment enregistré et transmis à l'équipe." : "Enregistrement en cours…"}</div>
        </ModalRegister>

        <ModalRegister open={showTrainingMonitor} title="Calibration IA — Module d'entraînement" onClose={() => setShowTrainingMonitor(false)} containerClassName="!max-w-[800px]" onHint={() => setShowHintModal(true)}>
          <TrainingMonitor
            sessionCode={code}
            onComplete={() => {
              missionResolve({ key: "s2.p3.calibration.found" });
              missionResolve({ key: "s2.p4.classifier.found" });
              toast?.success?.("Calibration terminée ! Code généré.");
            }}
          />
        </ModalRegister>

        {hasStressMonitor && (
          <ModalRegister open={showStressMonitor} title="Monitoring de Simulation RH" onClose={() => setShowStressMonitor(false)} containerClassName="!max-w-[1200px]" onHint={() => setShowHintModal(true)}>
            <StressMonitor simulationData={simulationData} code={code} pseudo={pseudo} missionResolve={missionResolve} />
          </ModalRegister>
        )}

        {/* ── Dashboard live — avec code d'erreur visible ── */}
        <ModalRegister open={showDashboardLive} title="Dashboard — Monitoring système" onClose={() => setShowDashboardLive(false)} containerClassName="!max-w-[1600px]">
          <div className="py-4 px-2">
            <div className="rounded-xl border border-cyan-500/30 bg-black p-5">
              <div className="mb-4">
                <div className="text-xs uppercase tracking-[0.2em] text-cyan-300 mb-2 flex items-center justify-between">
                  <span>Monitoring système</span>
                  <span className="text-emerald-400 animate-pulse">● LIVE</span>
                </div>
                <div className="text-xs text-slate-400">Visualisation en temps réel des services actifs.</div>
              </div>

              {/* ── Encadré anomalie critique — 5b dashboard utile ── */}
              <div className="mb-5 rounded-xl border-2 border-red-500/60 bg-red-500/10 p-4">
                <div className="flex items-start gap-3">
                  <div className="flex-1">
                    <div className="text-sm font-bold text-red-300 uppercase tracking-wider mb-1">
                      Anomalie critique enregistrée
                    </div>
                    <div className="flex items-center gap-3 flex-wrap">
                      <span className="font-mono text-lg font-bold text-red-200 tracking-widest">ERR-0x4A7C</span>
                      <span className="text-xs text-slate-400">— Détectée à 00:10, lors de l'arrêt des serveurs</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className={`grid grid-cols-1 gap-4 mb-6 ${hasStressMonitor ? "lg:grid-cols-2 xl:grid-cols-3" : "lg:grid-cols-2"}`}>
                {activeServices.map(({ key, label, color, cpuColor, memColor }) => (
                  <ServiceCard key={key} label={label} liveMetrics={liveMetrics[key]} metricsHistory={metricsHistory[key]} color={color} cpuColor={cpuColor} memColor={memColor} />
                ))}
                {stoppedServices.map(label => (
                  <ServiceCard key={label} label={label} color="red" stopped />
                ))}
              </div>

              <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-4">
                <div className="text-xs uppercase tracking-[0.2em] text-amber-300 mb-3">Statut système global</div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div><div className="text-slate-400 text-xs mb-1">Services actifs</div><div className="text-2xl font-bold text-emerald-400">4<span className="text-slate-600">/6</span></div></div>
                  <div><div className="text-slate-400 text-xs mb-1">Services arrêtés</div><div className="text-2xl font-bold text-red-400">2</div></div>
                  <div><div className="text-slate-400 text-xs mb-1">Charge moyenne</div><div className="text-2xl font-bold text-cyan-400">{avgCpu}%</div></div>
                  <div><div className="text-slate-400 text-xs mb-1">Alertes actives</div><div className="text-2xl font-bold text-amber-400">3</div></div>
                </div>
                <div className="mt-4 pt-4 border-t border-amber-500/20 space-y-2">
                  <div className="flex items-start gap-2 text-xs"><span className="text-red-400">⚠️</span><span className="text-slate-300">Service Communications : Arrêt inattendu (09:47)</span></div>
                  <div className="flex items-start gap-2 text-xs"><span className="text-red-400">⚠️</span><span className="text-slate-300">Service Évaluation RH : Arrêt inattendu (09:47)</span></div>
                  <div className="flex items-start gap-2 text-xs"><span className="text-amber-400">⚠️</span><span className="text-slate-300">Shutdown global initié par ORIGAMI (cause : seuil critique)</span></div>
                </div>
              </div>
            </div>
          </div>
        </ModalRegister>

        <ModalRegister open={showCarnet} title="Carnet de supervision" onClose={() => setShowCarnet(false)} containerClassName="!max-w-[700px]">
          <div className="py-4 px-2">
            <div className="rounded-xl border border-slate-700 bg-[#2a2520] text-slate-200 p-5">
              <div className="text-xs uppercase tracking-[0.2em] text-amber-300 mb-3">📓 Journal de bord — Supervision IA</div>
              <div className="space-y-3 text-sm">
                <div className="rounded-lg border border-cyan-500/20 bg-cyan-500/5 p-3">
                  <div className="text-xs text-cyan-300 font-mono mb-1">12/02/2025 - 14:30</div>
                  <div className="text-xs text-slate-300">"Tous les services fonctionnent nominalement. Pic de charge observé sur le module Empathie Cognitive (normal après calibration)."</div>
                </div>
                <div className="rounded-lg border border-amber-500/20 bg-amber-500/5 p-3">
                  <div className="text-xs text-amber-300 font-mono mb-1">13/02/2025 - 09:15</div>
                  <div className="text-xs text-slate-300">"Intégration Tango terminée. Système stable. ORIGAMI semble plus réactif aux messages marqués 'Executive' — à surveiller."</div>
                </div>
                <div className="rounded-lg border border-red-500/20 bg-red-500/5 p-3">
                  <div className="text-xs text-red-300 font-mono mb-1">Aujourd'hui - Informations temporelles manquantes</div>
                  <div className="text-xs text-slate-300">"⚠️ ALERTE : Shutdown inattendu de tous les services. ORIGAMI a initié une séquence d'extinction volontaire."</div>
                </div>
              </div>
            </div>
          </div>
        </ModalRegister>

        <ModalRegister open={showFeuilles} title="Protocoles de monitoring" onClose={() => setShowFeuilles(false)} containerClassName="!max-w-[650px]">
          <div className="py-4 px-2">
            <div className="rounded-xl border border-slate-700 bg-slate-900/60 p-5">
              <div className="text-xs uppercase tracking-[0.2em] text-slate-400 mb-3">📄 Feuille de procédure</div>
              <div className="rounded-lg border border-cyan-500/30 bg-cyan-500/5 p-4">
                <div className="text-sm font-semibold text-cyan-200 mb-3">Protocole d'analyse d'incident</div>
                <ol className="list-decimal list-inside space-y-2 text-xs text-slate-300">
                  <li>Consulter le panneau de contrôle pour identifier les plages horaires critiques</li>
                  <li>Filtrer les événements par code d'erreur et type d'alerte</li>
                  <li>Utiliser le code d'erreur pour accéder au terminal d'analyse</li>
                  <li>Reconstituer la séquence d'événements dans l'ordre chronologique</li>
                  <li>Analyser la chaîne d'exécution pour comprendre la cause racine</li>
                </ol>
                <div className="mt-3 pt-3 border-t border-cyan-500/20 text-xs text-slate-400">Les événements critiques sont marqués en rouge sur les graphiques.</div>
              </div>
            </div>
          </div>
        </ModalRegister>

        {/* ── Tiroir — #2847 uniquement (5a) ── */}
        <ModalRegister open={showTiroir} title="Tiroir — Archives d'incidents" onClose={() => setShowTiroir(false)} containerClassName="!max-w-[750px]">
          <div className="py-4 px-2">
            <div className="rounded-xl border border-slate-700 bg-slate-900/60 p-5">
              <div className="text-xs uppercase tracking-[0.2em] text-slate-400 mb-3">🗄️ Logs d'incidents</div>
              <div className="rounded-lg border border-red-500/30 bg-red-500/5 p-4">
                <div className="text-xs font-mono text-red-300 mb-2">Incident #2847 — Aujourd'hui</div>
                <div className="text-sm text-slate-200 leading-relaxed space-y-1">
                  <div><span className="font-semibold text-red-200">Extinction complète des systèmes.</span></div>
                  <div className="text-xs text-slate-400 space-y-0.5 mt-2">
                    <div>Initiateur : <span className="text-slate-300">ORIGAMI</span></div>
                    <div>Raison : <span className="text-slate-300">Seuil critique détecté (gratitude / toxicité)</span></div>
                    <div>Code d'anomalie : <span className="font-mono font-bold text-red-300">ERR-0x4A7C</span></div>
                    <div>Statut : <span className="text-amber-300 font-semibold">EN ANALYSE</span></div>
                  </div>
                </div>
                <div className="mt-3 pt-3 border-t border-red-500/20 text-xs text-slate-400">
                  Consulter le terminal d'analyse pour reconstituer la séquence complète.
                </div>
              </div>
            </div>
          </div>
        </ModalRegister>

        <HintsModalHub open={showHintModal} onClose={() => setShowHintModal(false)}
          title="Indices — Joueur 3 (Salle de Supervision IA)"
          markHintUsedOnce={markHintUsedOnce} categories={hintCategories}
          usedStorageKey={`hints_used:${code}:s2:p${playerId}`} />
      </div>
    </div>
  );
}